import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

export const ITEMS_PER_PAGE = [20, 50, 100];

function usePagination() {
  const location = useLocation();

  const queryString = new URLSearchParams(location.search);
  const pageParam = queryString.get('page');
  const itemsPerPageParam = queryString.get('limit');

  const [currentPage, setCurrentPage] = useState(pageParam ? parseInt(pageParam, 10) : 1);
  const [itemsPerPage, setItemsPerPage] = useState(
    itemsPerPageParam ? parseInt(itemsPerPageParam, 10) : ITEMS_PER_PAGE[0],
  );
  const [totalItems, setTotalItems] = useState(0);
  const [loadingTotalItems, setLoadingTotalItems] = useState(false);
  const [isPaging, setIsPaging] = useState(false);

  const totalPages = useMemo(() => {
    return Math.ceil(totalItems / itemsPerPage);
  }, [totalItems, itemsPerPage]);

  function goToNextPage() {
    if (isPaging) return;
    const newPage = currentPage + 1;
    setPage(newPage);
  }

  function goToPreviousPage() {
    if (isPaging) return;
    const newPage = Math.max(currentPage - 1, 1);
    setPage(newPage);
  }

  function setPage(pageNumber: number) {
    if (isPaging) return;
    setCurrentPage(pageNumber);
  }

  function resetPages() {
    setCurrentPage(1);
  }

  function getOffset() {
    return (currentPage - 1) * itemsPerPage;
  }

  return {
    currentPage,
    itemsPerPage,
    totalItems,
    loadingTotalItems,
    totalPages,
    isPaging,
    goToNextPage,
    goToPreviousPage,
    setItemsPerPage,
    setIsPaging,
    setPage,
    resetPages,
    setTotalItems,
    setLoadingTotalItems,
    getOffset,
  };
}

// Not needed for TypeScript, but still needed for PropTypes validation in React components that use this hook
export const paginationType = {
  currentPage: PropTypes.number,
  itemsPerPage: PropTypes.number,
  totalItems: PropTypes.number,
  totalPages: PropTypes.number,
  isPaging: PropTypes.bool,
  goToNextPage: PropTypes.func,
  goToPreviousPage: PropTypes.func,
  setItemsPerPage: PropTypes.func,
  setIsPaging: PropTypes.func,
  setPage: PropTypes.func,
  setTotalItems: PropTypes.func,
  getOffset: PropTypes.func,
  resetPages: PropTypes.func,
};

export default usePagination;
